import { extrudeKeyInfo, useToday, useV2GetCheckoutQuery, V2BundleFragment } from "@stepapp/core";
import { ApartmentType } from "@stepapp/core/apiTypes";
import { addDays, isBefore, isSameDay } from "date-fns";
import { useLocalSearchParams } from "expo-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "./redux";

export const useServiceDefaultValues = ({ bundle }: { bundle?: V2BundleFragment }) => {
  const { t } = useTranslation();
  const today = useToday();
  const invoiceData = useAppSelector(({ user }) => user.invoiceData);
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const { localizationId, city } = useAppSelector(({ appConfig }) => appConfig);
  const floorArea = useAppSelector(({ homeProfile }) => homeProfile.floorArea);

  const hasBillingAddress = useMemo(
    () => !!invoiceData?.taxIdentification,
    [invoiceData?.taxIdentification],
  );

  const initialBundleId = bundle?.id || "";

  const { data, loading } = useV2GetCheckoutQuery();

  const checkout = useMemo(() => data?.v2GetCheckout, [data?.v2GetCheckout]);
  const hasCheckoutSlug = !!checkout?.bundle?.slug;

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const hasDraftCheckout = useMemo(
    () => checkout?.bundle?.slug === slug && checkout?.locationId === localizationId,
    [hasCheckoutSlug, slug, localizationId],
  );

  const keyInfo = extrudeKeyInfo(checkout?.address?.notes) || "";
  const willCustomerBePresent =
    keyInfo === t("screens.serviceSummary.iWillBeThere") || ("deselected" as const);

  const defaultMeta = {
    timeConsumption: hasDraftCheckout ? checkout?.timeConsumption : "",
    valid: true,
    total: hasDraftCheckout ? checkout?.priceAfterDiscounts || 0 : 0,
    loading,
    detailsShown: false,
    willCustomerBePresent: hasDraftCheckout ? willCustomerBePresent : ("deselected" as const),
    keyInfo: hasDraftCheckout ? keyInfo : "",
    addInvoice: hasBillingAddress,
    homeTypePlaceholder: "",
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const defaultValues = useMemo(() => {
    const address = {
      city,
      doorCode: "",
      floorArea: floorArea || 0,
      notes: "",
      postalOrZipCode: "",
      streetAddress1: "",
      streetAddress2: "",
      type: ApartmentType.Apartment,
    };

    return {
      address,
      attributes: [],
      billingAddress: hasBillingAddress ? invoiceData : null,
      bundleId: initialBundleId,
      couponCode: null,
      houseArea: floorArea || null,
      locationId: localizationId,
      paymentMethodId: null,
      propertyId: null,
      services: [],
      startDate: null,
      subscriptionOptionId: null,
      meta: defaultMeta,
    };
  }, [initialBundleId]);

  const attributes = useMemo(() => {
    return (
      checkout?.attributes.map((a) => ({
        attributeId: a?.attribute.id,
        valueId: a?.values.map((value) => value?.id),
        quantity: a?.quantity,
      })) || []
    );
  }, [checkout?.attributes]);

  // Remove free service variants
  const servicesToOrder = checkout?.services?.filter(
    (service) => !service?.variants?.some((variant) => variant?.isFree),
  );

  const services = useMemo(() => {
    return (
      servicesToOrder?.map((s) => ({
        serviceId: s?.service.id,
        variants: s?.variants.map((variant) => ({
          quantity: variant.quantity,
          variantId: variant.variantId,
        })),
      })) || []
    );
  }, [servicesToOrder]);

  // Check for outdated values for attributes in current bundle
  const filteredAttributes = useMemo(() => {
    return attributes?.reduce(
      (acc, attr) => {
        const findQuestion = (id: string) => {
          return (
            bundle?.questions?.find((q) => q?.id === id) ||
            bundle?.categories?.flatMap((cat) => cat?.questions || []).find((q) => q?.id === id)
          );
        };

        const question = findQuestion(attr?.attributeId || "");

        if (question) {
          const validValueIds =
            attr?.valueId
              ?.filter((valueId) => question?.answers?.some((answer) => answer?.id === valueId))
              ?.filter((valueId): valueId is string => valueId !== undefined) || undefined;

          if (validValueIds && validValueIds.length > 0 && attr?.attributeId && attr?.quantity) {
            acc.push({
              attributeId: attr?.attributeId,
              valueId: validValueIds,
              quantity: attr?.quantity,
            });
          }
        }
        return acc;
      },
      [] as { attributeId: string; quantity: number; valueId: string[] }[],
    );
  }, [attributes, bundle?.questions, bundle?.categories]);

  // Check for outdated values for services in current bundle
  const filteredServices = useMemo(() => {
    return services?.reduce(
      (acc, service) => {
        const serviceDetail = bundle?.services?.find((s) => s?.id === service?.serviceId);

        if (serviceDetail) {
          const validVariants = service?.variants?.filter((v) =>
            serviceDetail?.variants?.some((variant) => variant?.id === v?.variantId),
          );

          if (validVariants && validVariants.length > 0 && service?.serviceId) {
            acc.push({
              serviceId: service?.serviceId,
              variants: validVariants,
            });
          }
        }
        return acc;
      },
      [] as { serviceId: string; variants: { quantity: number; variantId: string }[] }[],
    );
  }, [services, bundle?.services]);

  const tomorrow = addDays(today, 1);
  const isPastDay =
    isSameDay(tomorrow, checkout?.startDate || "") || isBefore(checkout?.startDate || "", tomorrow);

  const startDate = isPastDay ? null : checkout?.startDate;

  const hasSubscriptionOptions = (bundle?.subscriptionOptions?.length || 0) > 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const draftCheckoutValues = useMemo(() => {
    const address = {
      city,
      doorCode: checkout?.address?.doorCode,
      floorArea: checkout?.address?.floorArea || 0,
      notes: checkout?.address?.notes,
      postalOrZipCode: checkout?.address?.postalOrZipCode,
      streetAddress1: checkout?.address?.streetAddress1,
      streetAddress2: checkout?.address?.streetAddress2,
      type: checkout?.address?.type || ApartmentType.Apartment,
    };

    return {
      address,
      attributes: initialBundleId ? filteredAttributes : attributes,
      billingAddress: checkout?.billingAddress,
      bundleId: checkout?.bundle?.id,
      couponCode: checkout?.couponCode,
      customerNote: checkout?.customerNote,
      houseArea: checkout?.address?.floorArea,
      locationId: localizationId,
      paymentMethodId: checkout?.paymentMethodId,
      propertyId: checkout?.propertyId || null,
      services: initialBundleId ? filteredServices : services,
      startDate,
      subscriptionOptionId: hasSubscriptionOptions ? checkout?.periodicity?.id : null,
      meta: defaultMeta,
    };
  }, [checkout?.id, initialBundleId]);

  return hasDraftCheckout ? draftCheckoutValues : defaultValues;
};
